import { useState, useEffect } from 'react';

import { LoadingButton } from '@mui/lab';
import { Dialog, Button, TextField, DialogTitle, DialogContent, DialogActions } from '@mui/material';

import { useTranslate } from 'src/locales';

// ----------------------------------------------------------------------

type Props = {
  open: boolean;
  onClose: VoidFunction;
  /** The title set on this record, or empty when it uses the document's own name. */
  currentTitle: string;
  /** The document's own name, shown as the placeholder and restored when the field is cleared. */
  originalTitle: string;
  loading?: boolean;
  /** Receives the trimmed title, or null to restore the original name. */
  onSubmit: (title: string | null) => void;
};

export default function RenameDocumentDialog({
  open,
  onClose,
  currentTitle,
  originalTitle,
  loading = false,
  onSubmit,
}: Props) {
  const { t } = useTranslate();

  const [title, setTitle] = useState(currentTitle);

  useEffect(() => {
    if (open) {
      setTitle(currentTitle);
    }
  }, [open, currentTitle]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('profile.documents.rename_modal.title')}</DialogTitle>

      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          sx={{ mt: 1 }}
          label={t('profile.documents.title_field')}
          value={title}
          placeholder={originalTitle}
          onChange={(event) => setTitle(event.target.value)}
          inputProps={{ maxLength: 255 }}
          helperText={t('profile.documents.title_hint')}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <LoadingButton variant="contained" loading={loading} onClick={() => onSubmit(title.trim() || null)}>
          {t('common.save')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
